import { Form, Input, Modal, Switch, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { LeaveType } from 'types/leave-request';
import { mapLeaveRequestError } from 'pages/leave-request-manager/helpers/leave-request-error';

interface Props {
  open: boolean;
  leaveType: LeaveType | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface LeaveTypeForm {
  name: string;
  description?: string;
  isActive: boolean;
}

const LeaveTypeModal = ({ open, leaveType, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm<LeaveTypeForm>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: leaveType?.name || '',
      description: leaveType?.description || '',
      isActive: leaveType?.isActive ?? true,
    });
  }, [form, leaveType, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: LeaveTypeForm) => {
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      isActive: values.isActive,
    };

    setLoading(true);
    try {
      if (leaveType) {
        await AxiosUtils.patch(`${API.leaveTypes}/${leaveType.id}`, payload);
        notification.success({ message: 'Đã cập nhật loại nghỉ phép.' });
      } else {
        await AxiosUtils.post(API.leaveTypes, payload);
        notification.success({ message: 'Đã tạo loại nghỉ phép.' });
      }
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapLeaveRequestError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={leaveType ? 'Sửa loại nghỉ phép' : 'Tạo loại nghỉ phép'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="custom-form"
        initialValues={{ isActive: true }}
      >
        <Form.Item
          label="Tên loại nghỉ"
          name="name"
          normalize={(value) => (typeof value === 'string' ? value.trimStart() : value)}
          rules={[
            { required: true, message: 'Vui lòng nhập tên loại nghỉ.' },
            { max: 120, message: 'Tên loại nghỉ tối đa 120 ký tự.' },
            {
              validator: (_, value) =>
                value?.trim()
                  ? Promise.resolve()
                  : Promise.reject(new Error('Vui lòng nhập tên loại nghỉ.')),
            },
          ]}
        >
          <Input maxLength={120} showCount placeholder="VD: Nghỉ phép năm" />
        </Form.Item>

        <Form.Item
          label="Mô tả"
          name="description"
          rules={[{ max: 500, message: 'Mô tả tối đa 500 ký tự.' }]}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="VD: 12 ngày/năm, cộng dồn theo tháng"
          />
        </Form.Item>

        <Form.Item label="Trạng thái" name="isActive" valuePropName="checked">
          <Switch checkedChildren="Đang dùng" unCheckedChildren="Tắt" />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={leaveType ? 'Lưu' : 'Tạo'}
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default LeaveTypeModal;
